import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import { useThemeContext } from '@honda-canada/design-system-react';
import ModelPageNav from './ModelPageNav';
import { PSP_SITE_NAME, HONDA_SITE_NAME, ACURA_SITE_NAME, GLOBAL_NAV_MOBILE_HEIGHT, GLOBAL_NAV_DESKTOP_HEIGHT, PE_SITE_NAME, } from '../../utils/constants';
import { useAppName } from '../../utils/sitecoreContext';
import { variant1, variant2, variant3 } from './constants';
import parseSitecoreFields from './parseSitecoreFields';
import { ModelExplorationContext } from '../ModelExplorationContext';
const isModelYear = (value) => !!value && 'legacyPageUrl' in value;
export const getLegacyPageUrl = (option, options) => {
    const foundOption = options === null || options === void 0 ? void 0 : options.find(v => v.name === option.name);
    return isModelYear(foundOption) ? foundOption.legacyPageUrl.value : undefined;
};
/**
 * Returns a url that corresponds to the currently-displayed page. For example,
 * when the current path is /ILX/Trims, the 2021 dropdown item should link to
 * /ILX/2021/Trims instead of just /ILX/2021.
 * @param {string} option - a model year or subCategory
 * @param {array} navItems - an array of nav items, each with a url
 * @param {string} pathname - the current pathname, from react-router-dom
 * @returns a corresponding URL for the given year/subCategory
 */
export const getCorrespondingUrl = (option, navItems, pathname = '') => {
    var _a, _b;
    const currentPage = navItems === null || navItems === void 0 ? void 0 : navItems.find(navItem => { var _a, _b; return (_a = pathname === null || pathname === void 0 ? void 0 : pathname.toLowerCase()) === null || _a === void 0 ? void 0 : _a.includes((_b = navItem === null || navItem === void 0 ? void 0 : navItem.url) === null || _b === void 0 ? void 0 : _b.toLowerCase()); });
    const currentPageName = currentPage === null || currentPage === void 0 ? void 0 : currentPage.name;
    const correspondingSubPage = (_a = option === null || option === void 0 ? void 0 : option.subPages) === null || _a === void 0 ? void 0 : _a.find(s => s.name.toLowerCase() === (currentPageName === null || currentPageName === void 0 ? void 0 : currentPageName.toLowerCase()));
    return (_b = correspondingSubPage === null || correspondingSubPage === void 0 ? void 0 : correspondingSubPage.url) === null || _b === void 0 ? void 0 : _b.toLowerCase();
};
export const getNavItems = (modelNav) => modelNav === null || modelNav === void 0 ? void 0 : modelNav.map(navItem => {
    var _a, _b, _c, _d, _e;
    // If a nav item has no nameBadge image, ExperienceEditor adds a placeholder image,
    // which we don't want because it prevents the text value from showing up.
    if (((_b = (_a = navItem.nameBadge) === null || _a === void 0 ? void 0 : _a.item) === null || _b === void 0 ? void 0 : _b.editable) && ((_e = (_d = (_c = navItem.nameBadge) === null || _c === void 0 ? void 0 : _c.item) === null || _d === void 0 ? void 0 : _d.value) === null || _e === void 0 ? void 0 : _e.class) === 'scEmptyImage') {
        // FIXME: consider non-mutating method
        // eslint-disable-next-line no-param-reassign
        delete navItem.nameBadge.item.value.src;
    }
    return navItem;
});
export const getYears = (fieldsValue, modelYears, navItems, pathname) => {
    var _a, _b;
    return (_b = (_a = fieldsValue === null || fieldsValue === void 0 ? void 0 : fieldsValue.years) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.map(year => ({
        ...year,
        legacyPageUrl: getLegacyPageUrl(year, modelYears),
        correspondingUrl: getCorrespondingUrl(year, navItems, pathname),
    }));
};
const getNodes = (items) => {
    if (!items)
        return {};
    return items.map(item => ({
        name: item.name,
        url: item.url,
    }));
};
const createGetSelectedOptionItem = (appName) => (dropdownOptions, subCategory, modelYear) => {
    const isPe = appName === PE_SITE_NAME;
    const selectedOption = isPe ? subCategory.toLowerCase() : modelYear;
    const selectedOptionItem = isPe
        ? dropdownOptions === null || dropdownOptions === void 0 ? void 0 : dropdownOptions.find((y) => { var _a, _b; return ((_b = (_a = y === null || y === void 0 ? void 0 : y.modelNav) === null || _a === void 0 ? void 0 : _a.overviewPage) === null || _b === void 0 ? void 0 : _b.name.toLowerCase()) === selectedOption; })
        : dropdownOptions === null || dropdownOptions === void 0 ? void 0 : dropdownOptions.find((y) => y.name.toLowerCase() === selectedOption);
    return selectedOptionItem;
};
const ModelPageNavWrapper = ({ fields, rendering }) => {
    var _a;
    const appName = useAppName();
    const header = useThemeContext('header');
    const taggedFieldsValue = useMemo(() => {
        var _a;
        const fieldsValue = (_a = fields === null || fields === void 0 ? void 0 : fields.data) === null || _a === void 0 ? void 0 : _a.value;
        if (typeof fieldsValue === 'object' && fieldsValue !== null)
            return {
                ...fieldsValue,
                appName, // add appName to build tagged union
            };
        return null;
    }, [(_a = fields === null || fields === void 0 ? void 0 : fields.data) === null || _a === void 0 ? void 0 : _a.value, appName]);
    // TODO: only for PSP, read trim(gtm model) from URL
    const location = useLocation();
    const pathname = location === null || location === void 0 ? void 0 : location.pathname;
    const [currentTrim, setCurrentTrim] = useState('');
    const getSelectedOptionItem = createGetSelectedOptionItem(appName);
    const parsedFields = parseSitecoreFields(taggedFieldsValue, pathname);
    useEffect(() => {
        const searchParams = new URLSearchParams(location === null || location === void 0 ? void 0 : location.search);
        const trimFromUrl = searchParams.get('trim');
        setCurrentTrim(trimFromUrl || '');
    }, [location]);
    // TODO: consider moving this to ModelExplorationContext
    const modelExplorationContext = useContext(ModelExplorationContext) || {};
    useEffect(() => {
        const { navItems: navigationItems, setNavItems } = modelExplorationContext;
        if (!isEqual(getNodes(parsedFields === null || parsedFields === void 0 ? void 0 : parsedFields.navItems), getNodes(navigationItems))) {
            if (typeof setNavItems === 'function')
                setNavItems(getNodes(parsedFields === null || parsedFields === void 0 ? void 0 : parsedFields.navItems));
        }
    }, [parsedFields === null || parsedFields === void 0 ? void 0 : parsedFields.navItems, modelExplorationContext === null || modelExplorationContext === void 0 ? void 0 : modelExplorationContext.navigationItems, modelExplorationContext === null || modelExplorationContext === void 0 ? void 0 : modelExplorationContext.setNavItems]);
    if (!parsedFields)
        return null;
    const { modelPageSettings, navItems, categoryName, dropdownOptions, modelName, gtmModelName: gtmModelNameFromSitecore, } = parsedFields;
    let desktopStickyTopPosition = header.desktop.stickyHeight || header.desktop.height;
    let mobileStickyTopPosition = header.mobile.height;
    let variant;
    let gtmModelName = gtmModelNameFromSitecore;
    switch (appName) {
        case HONDA_SITE_NAME:
        case ACURA_SITE_NAME:
            // site core header height have been set to Acura/Honda Header to handle stickyness .
            desktopStickyTopPosition = GLOBAL_NAV_DESKTOP_HEIGHT;
            mobileStickyTopPosition = GLOBAL_NAV_MOBILE_HEIGHT;
            variant = variant2;
            break;
        case PSP_SITE_NAME:
            variant = variant1;
            // TODO: DSR needs to be updated.
            // there is no legacy bar above PSP Header.
            desktopStickyTopPosition = 0;
            mobileStickyTopPosition = 0;
            // TODO: only for PSP, gtmModelName is different from modelName
            gtmModelName = currentTrim;
            break;
        case PE_SITE_NAME:
            variant = variant3;
            break;
        default:
            variant = variant2;
            break;
    }
    return (React.createElement(ModelPageNav, { variant: variant, modelPageSettings: modelPageSettings, vehicleType: "", navItems: navItems, dropdownOptions: dropdownOptions, rendering: rendering, categoryName: categoryName, modelName: modelName, desktopStickyTopPosition: desktopStickyTopPosition, mobileStickyTopPosition: mobileStickyTopPosition, gtm: { gtmModelName }, getSelectedOptionItem: getSelectedOptionItem }));
};
export default ModelPageNavWrapper;
